import React from 'react';
import { Link } from 'react-router-dom';
import PageHero from '../components/PageHero';
import {
  IconCpu, IconLeaf, IconCheckCircle, IconShield, IconBarChart,
  IconTarget, IconPin,
} from '../components/Icons';
import './servicios/ServicePage.css';

const PRODUCTS = [
  {
    id: 'minero',
    Icon: IconCpu,
    color: 'green',
    title: 'Software Minero',
    desc: 'Plataforma de escritorio de nivel profesional desarrollada internamente por Impacta M.A. SAS para la gestión integral de operaciones mineras subterráneas. Integra planificación, geomecánica, ventilación, control de gases, gestión de equipos y seguridad industrial en un único sistema.',
    details: [
      'Planificación minera: método de explotación y diseño de voladuras',
      'Redes de ventilación principal y auxiliar con monitoreo de gases',
      'Sostenimiento subterráneo: pernos, cables, arcos y madera',
      'Gestión de equipos: bombas, compresores y sistemas eléctricos',
      'Economía minera: VPN, TIR y análisis de sensibilidad',
      'Reportes automáticos en Excel, PDF y Word para la ANM',
    ],
    norm: 'Decreto 1886/2015 – Decreto 944/2022 – Ley 685/2001',
    path: '/software/minero',
  },
  {
    id: 'ambiental',
    Icon: IconLeaf,
    color: 'blue',
    title: 'Software Ambiental',
    desc: 'Herramienta especializada para la gestión ambiental de proyectos mineros e industriales. Automatiza el seguimiento de obligaciones ambientales, el cálculo de indicadores y la generación de informes para corporaciones autónomas regionales y el ANLA.',
    details: [
      'Seguimiento y control de obligaciones de licencias ambientales',
      'Cálculo de indicadores de gestión ambiental y biodiversidad',
      'Monitoreo de calidad de agua, suelo y aire con alertas',
      'Generación automática de ICAS y reportes de cumplimiento',
      'Gestión de Planes de Manejo Ambiental (PMA)',
      'Trazabilidad de datos con base de datos cifrada (SQLCipher)',
    ],
    norm: 'Decreto 1076/2015 – Ley 99/1993 – Resoluciones ANLA',
    path: '/software/ambiental',
  },
];

const HIGHLIGHTS = [
  {
    Icon: IconShield,
    color: 'green',
    title: 'Seguridad de datos',
    desc: 'Base de datos cifrada con SQLCipher y autenticación biométrica facial para proteger la información técnica.',
  },
  {
    Icon: IconBarChart,
    color: 'blue',
    title: 'Reportes automáticos',
    desc: 'Generación automática de documentos técnicos en Excel, PDF y Word listos para presentación ante autoridades.',
  },
  {
    Icon: IconTarget,
    color: 'green',
    title: 'Normativa colombiana',
    desc: 'Desarrollado con los estándares y decretos vigentes del sector minero-ambiental colombiano.',
  },
  {
    Icon: IconCpu,
    color: 'blue',
    title: 'Desarrollo propio',
    desc: 'Software creado por el equipo de Impacta M.A. SAS, respaldado por años de experiencia en campo.',
  },
];

export default function SoftwarePage() {
  return (
    <div>
      <PageHero
        title="Software Especializado"
        subtitle="Herramientas de ingeniería desarrolladas por Impacta M.A. SAS para la gestión técnica y normativa de proyectos mineros y ambientales."
        variant="green"
      />

      {/* ── Intro ── */}
      <section className="section">
        <div className="container">
          <div className="svc-intro">
            <div className="svc-intro__text">
              <div className="divider" />
              <h2 className="section-title">Tecnología al Servicio de la Minería</h2>
              <p>
                En <strong>Impacta M.A. SAS</strong> no solo prestamos servicios de consultoría:
                también desarrollamos nuestras propias herramientas de software para garantizar
                cálculos precisos, trazables y alineados con la normativa colombiana vigente.
              </p>
              <p>
                Nuestras plataformas están diseñadas para ingenieros y técnicos del sector,
                eliminando errores manuales y reduciendo el tiempo de elaboración de informes
                técnicos ante la <strong>ANM</strong>, <strong>ANLA</strong> y demás autoridades.
              </p>
            </div>
            <div className="svc-norm-card">
              <h3>Beneficios del software</h3>
              <ul>
                <li>Reduce errores en cálculos técnicos</li>
                <li>Ahorra tiempo en elaboración de informes</li>
                <li>Cumplimiento normativo garantizado</li>
                <li>Trazabilidad total de los datos</li>
                <li>Reportes listos para entregar a autoridades</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Products ── */}
      <section className="section section--bg">
        <div className="container">
          <div className="divider" />
          <h2 className="section-title">Nuestros Productos de Software</h2>
          <div className="svc-list">
            {PRODUCTS.map(({ id, Icon, color, title, desc, details, norm, path }) => (
              <div key={id} className="svc-item">
                <div className={`svc-item__icon${color === 'blue' ? ' svc-item__icon--blue' : ''}`}>
                  <Icon size={28} />
                </div>
                <div className="svc-item__body">
                  <h3>{title}</h3>
                  <p className="svc-item__desc">{desc}</p>
                  <div className="svc-item__details">
                    <h4>Funcionalidades principales</h4>
                    <ul>
                      {details.map(d => (
                        <li key={d}>
                          <IconCheckCircle
                            size={15}
                            style={{
                              color: color === 'blue' ? 'var(--c-blue-500)' : 'var(--c-green-500)',
                              flexShrink: 0,
                              marginTop: 2,
                            }}
                          />
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="svc-item__footer">
                    <span className="svc-norm-badge">
                      <IconPin size={14} />
                      {norm}
                    </span>
                    <Link
                      to={path}
                      className={`btn btn--sm${color === 'blue' ? ' btn--blue' : ' btn--primary'}`}
                    >
                      Ver más detalles
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Highlights ── */}
      <section className="section">
        <div className="container">
          <div className="divider divider--center" />
          <h2 className="section-title section-title--center">¿Por qué nuestro software?</h2>
          <div className="sw-highlights" style={{ marginTop: '40px' }}>
            {HIGHLIGHTS.map(({ Icon, color, title, desc }) => (
              <div key={title} className="sw-highlight card card--elevated">
                <div className={`sw-highlight__icon sw-highlight__icon--${color}`}>
                  <Icon size={26} />
                </div>
                <h4>{title}</h4>
                <p>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="section section--dark">
        <div className="container" style={{ textAlign: 'center' }}>
          <div className="divider divider--center" style={{ background: 'var(--c-green-400)' }} />
          <h2 className="section-title section-title--white" style={{ marginBottom: '12px' }}>
            ¿Le interesa nuestro software?
          </h2>
          <p className="section-lead section-lead--center section-lead--white" style={{ marginBottom: '32px' }}>
            Contáctenos para una demostración personalizada o para conocer los planes de licenciamiento disponibles.
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contacto" className="btn btn--primary btn--lg">Solicitar Demostración</Link>
            <Link to="/software/minero" className="btn btn--ghost-white btn--lg">Ver Software Minero</Link>
            <Link to="/software/ambiental" className="btn btn--ghost-white btn--lg">Ver Software Ambiental</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
